import { useId } from "react";

import { FigureFrame } from "./FigureFrame";
import type { ResearchFigureProps } from "./types";

const ink = "var(--figure-ink, currentColor)";
const accent = "var(--figure-accent, currentColor)";
const surface = "var(--figure-surface, transparent)";

export function WeatherFigure({
  className,
  caption,
  title = "Weather, interaction spaces, and mobile app engagement",
  description = "A conceptual framework connects three interaction-space types and three weather conditions to app usage frequency and session duration. Selected reported effects are listed below.",
}: ResearchFigureProps) {
  const markerId = `weather-arrow-${useId().replace(/:/g, "")}`;

  return (
    <FigureFrame
      className={className}
      caption={caption}
      title={title}
      description={description}
      viewBox="0 0 760 500"
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={accent} />
        </marker>
      </defs>

      <g fill="none" stroke={ink} strokeWidth="1.25" vectorEffect="non-scaling-stroke">
        <rect x="32" y="58" width="260" height="178" fill={surface} />
        <rect x="468" y="88" width="260" height="118" fill={surface} />
        <path d="M 306 147 H 448" stroke={accent} markerEnd={`url(#${markerId})`} />
      </g>

      <g fill={ink}>
        <text x="32" y="31" fontSize="12" fontWeight="650" letterSpacing="1.6">
          INPUTS
        </text>
        <text x="52" y="85" fontSize="18" fontWeight="650">
          Interaction spaces
        </text>
        <text x="52" y="116" fontSize="14">
          Recreational · trails
        </text>
        <text x="52" y="145" fontSize="14">
          Functional · pet care
        </text>
        <text x="52" y="174" fontSize="14">
          Community · pet-friendly
        </text>
        <line x1="52" y1="193" x2="272" y2="193" stroke={ink} strokeOpacity=".28" />
        <text x="52" y="218" fontSize="13">
          Weather · heat · rain · wind
        </text>

        <text x="468" y="61" fontSize="12" fontWeight="650" letterSpacing="1.6">
          OUTCOME
        </text>
        <text x="490" y="121" fontSize="18" fontWeight="650">
          Mobile app engagement
        </text>
        <text x="490" y="154" fontSize="14">
          Usage frequency
        </text>
        <text x="490" y="181" fontSize="14">
          Session duration
        </text>
      </g>

      <g transform="translate(32 285)">
        <text fill={ink} x="0" y="0" fontSize="12" fontWeight="650" letterSpacing="1.6">
          REPORTED EFFECTS
        </text>
        <line x1="0" y1="18" x2="696" y2="18" stroke={ink} strokeWidth="1.25" />

        <g fill={ink} fontSize="14">
          <text x="0" y="54">Functional spaces</text>
          <text x="394" y="54">usage</text>
          <text x="696" y="54" textAnchor="end" fontWeight="700" fill={accent}>
            +27.8%
          </text>

          <text x="0" y="91">Heavy rain</text>
          <text x="394" y="91">usage / session</text>
          <text x="696" y="91" textAnchor="end" fontWeight="700" fill={accent}>
            −6.2% / −12.7%
          </text>

          <text x="0" y="128">Recreational spaces</text>
          <text x="394" y="128">session</text>
          <text x="696" y="128" textAnchor="end" fontWeight="700" fill={accent}>
            +6.6%
          </text>

          <text x="0" y="165">Community spaces</text>
          <text x="394" y="165">session</text>
          <text x="696" y="165" textAnchor="end" fontWeight="700" fill={accent}>
            +4.3%
          </text>

          <text x="0" y="202">Heatwave / strong wind</text>
          <text x="394" y="202">session</text>
          <text x="696" y="202" textAnchor="end" fontWeight="700" fill={accent}>
            −5.2% / no effect
          </text>
        </g>

        <g stroke={ink} strokeOpacity=".18">
          <line x1="0" y1="67" x2="696" y2="67" />
          <line x1="0" y1="104" x2="696" y2="104" />
          <line x1="0" y1="141" x2="696" y2="141" />
          <line x1="0" y1="178" x2="696" y2="178" />
        </g>
      </g>
    </FigureFrame>
  );
}
